// FacultyLeaveManagement.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { baseApiURL } from "../../baseUrl";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";

const FacultyLeaveManagement = ({ onClose, onSuccess, setSelectedMenu }) => {
  const router = useLocation();
  const [faculties, setFaculties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateOptions, setDateOptions] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [leaveType, setLeaveType] = useState("Casual Leave");
  const [reason, setReason] = useState("");
  const [selectedSubstitute, setSelectedSubstitute] = useState("");
  const [currentFaculty, setCurrentFaculty] = useState(null);
  const [leaveRecords, setLeaveRecords] = useState([]);
  const [activeTab, setActiveTab] = useState("request");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSubstituteModal, setShowSubstituteModal] = useState(false);
  const [leaveToCancel, setLeaveToCancel] = useState(null);
  const [activeLeaveForSubstitute, setActiveLeaveForSubstitute] = useState(null);
  const [quotas, setQuotas] = useState({});

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const leaveTypes = [
    "Casual Leave", 
    "Earned Leave", 
    "Medical Leave",
    "Sick Leave",
    "Vacation Leave", 
    "Commuted Leave (Half Pay Leave)", 
    "Maternity Leave", 
    "Study Leave", 
    "Sabbatical Leave", 
    "Overseas Assignment Leave",
    "Half Day Leave",
    "Optional Leave", 
    "Paternity Leave", 
    "Duty Leave"
  ];

  const [holidays, setHolidays] = useState([]);

  useEffect(() => {
    fetchCurrentFaculty();
    fetchAllFaculties();
    fetchLeaveHistory();
    fetchMonthlyHolidays();
    fetchQuotas();
  }, []);

  const fetchMonthlyHolidays = async () => {
    try {
      const today = new Date();
      const month = today.getMonth() + 1;
      const year = today.getFullYear();
      const response = await axios.get(`${baseApiURL()}/accounts/attendance/config?month=${month}&year=${year}`);
      if (response.data.success && response.data.config) {
        setHolidays(response.data.config.globalHolidays.map(h => h.date));
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
    }
  };

  useEffect(() => {
    generateDateOptions();
  }, [holidays]);

  const fetchCurrentFaculty = async () => {
    if (!router.state?.loginid) return;
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${baseApiURL()}/faculty/details/getDetails`, { employeeId: router.state.loginid }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) setCurrentFaculty(response.data.user[0]);
    } catch (error) {
      console.error("Error fetching current faculty:", error);
    }
  };

  const fetchAllFaculties = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${baseApiURL()}/faculty/details/getDetails2`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const otherFaculties = response.data.faculties.filter(f => f.employeeId !== router.state?.loginid);
        setFaculties(otherFaculties);
      }
    } catch (error) {
      console.error("Error fetching faculties:", error);
    }
  };

  const fetchLeaveHistory = async () => {
    if (!router.state?.loginid) return;
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${baseApiURL()}/faculty/leave/getLeaves/${router.state.loginid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) setLeaveRecords(response.data.leaves);
    } catch (error) {
      console.error("Error fetching leave history:", error);
    }
  };

  const fetchQuotas = async () => {
    try {
      const year = new Date().getFullYear();
      const token = localStorage.getItem("token");
      const response = await axios.get(`${baseApiURL()}/faculty/leave/quotas?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.quotas) {
        setQuotas(response.data.quotas);
      }
    } catch (error) {
      console.error("Error fetching quotas:", error);
    }
  };

  const generateDateOptions = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateString = date.toISOString().split('T')[0];

      // Skip Sundays and Global Holidays
      if (date.getDay() !== 0 && !holidays.includes(dateString)) {
        const dayName = daysOfWeek[date.getDay() - 1];
        dates.push({ date: dateString, day: dayName, display: `${dateString} (${dayName})` });
      }
    }
    setDateOptions(dates);
  };

  const handleDateSelect = (date) => {
    setSelectedDates(prev => prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]);
  };

  const submitLeaveRequest = async () => {
    if (selectedDates.length === 0 || !reason) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    toast.loading("Submitting leave request...");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${baseApiURL()}/faculty/leave/request`, {
        facultyId: router.state.loginid,
        facultyName: currentFaculty ? `${currentFaculty.firstName} ${currentFaculty.lastName}` : "",
        dates: selectedDates,
        leaveType,
        startDate: selectedDates[0],
        endDate: selectedDates[selectedDates.length - 1],
        reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.dismiss();
      setLoading(false);

      if (response.data.success) {
        toast.success("Leave request submitted! Pending HOD approval.");
        setSelectedDates([]);
        setReason("");
        fetchLeaveHistory();
        if (onSuccess) onSuccess();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.dismiss();
      setLoading(false);
      toast.error("Failed to submit leave request");
    }
  };

  const handleAssignSubstitute = async () => {
    if (!selectedSubstitute) {
      toast.error("Please select a substitute");
      return;
    }

    const substitute = faculties.find(f => f.employeeId === selectedSubstitute);
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${baseApiURL()}/faculty/leave/assignSubstitute/${activeLeaveForSubstitute._id}`, {
        substituteId: selectedSubstitute,
        substituteName: `${substitute.firstName} ${substitute.lastName}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success("Substitute assigned! Redirecting to your timetable...");
        setShowSubstituteModal(false);
        fetchLeaveHistory();

        // Redirect to MyFacultyTimeTable after a short delay
        if (setSelectedMenu && activeLeaveForSubstitute.dates && activeLeaveForSubstitute.dates.length > 0) {
          setTimeout(() => {
            // Get the day of the week for the first leave date
            const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const firstDate = new Date(activeLeaveForSubstitute.dates[0]);
            const dayName = daysOfWeek[firstDate.getDay()];

            // Redirect using setSelectedMenu
            setSelectedMenu("MyFacultyTimeTable");

            // Store context in session storage so MyFacultyTimeTable can read it
            sessionStorage.setItem("initialTimetableDay", dayName);
            sessionStorage.setItem("initialTimetableDate", activeLeaveForSubstitute.dates[0]);
            sessionStorage.setItem("substitutingForLeaveId", activeLeaveForSubstitute._id);
            sessionStorage.setItem("substitutingForFacultyId", router.state.loginid);
          }, 1500);
        }
      }
    } catch (error) {
      toast.error("Failed to assign substitute");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800";
      case "approved_by_hod": return "bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800";
      case "approved_by_principal": return "bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800";
      case "confirmed": return "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800";
      case "rejected": return "bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800";
      default: return "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300";
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Leave Management</h2>
          <p className="text-slate-500 dark:text-slate-400 font-medium text-xs mt-0.5">Submit leave requests and manage substitute allocations</p>
        </div>
        {onClose && <button onClick={onClose} className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 cursor-pointer font-bold">✕</button>}
      </div>

      <div className="flex border-b border-slate-100 dark:border-slate-800">
        <button
          className={`px-4 py-2 text-xs font-bold transition cursor-pointer ${
            activeTab === "request"
              ? "border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400 font-bold"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
          }`}
          onClick={() => setActiveTab("request")}
        >
          Request Leave
        </button>
        <button
          className={`px-4 py-2 text-xs font-bold transition cursor-pointer ${
            activeTab === "history"
              ? "border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400 font-bold"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
          }`}
          onClick={() => setActiveTab("history")}
        >
          Leave History
        </button>
      </div>

      {activeTab === "request" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Leave Type</label>
              <select
                value={leaveType}
                onChange={(e) => setLeaveType(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                {leaveTypes.map((type) => {
                  const used = leaveRecords
                    .filter(l => l.leaveType === type && (l.status === 'confirmed' || l.status === 'approved_by_principal'))
                    .reduce((acc, l) => acc + (l.leaveType === 'Half Day Leave' ? 0.5 : l.dates.length), 0);
                  
                  const quotaKey = type === "Medical Leave" || type === "Sick Leave" ? "Medical Leave" : type;
                  const total = quotas[quotaKey] || 0;

                  return (
                    <option key={type} value={type}>
                      {type} {total > 0 ? `(${used}/${total})` : (used > 0 ? `(Used: ${used})` : '')}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Reason</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                placeholder="Reason for leave"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Select Dates</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-3 border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 rounded-xl">
              {dateOptions.map(d => (
                <label
                  key={d.date}
                  className={`flex items-center p-2.5 border rounded-xl cursor-pointer text-xs transition ${
                    selectedDates.includes(d.date)
                      ? "bg-indigo-50 dark:bg-indigo-950/60 border-indigo-300 dark:border-indigo-700 text-indigo-900 dark:text-indigo-200 font-bold"
                      : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300"
                  }`}
                >
                  <input type="checkbox" checked={selectedDates.includes(d.date)} onChange={() => handleDateSelect(d.date)} className="mr-2" />
                  <span className="text-xs">{d.display}</span>
                </label>
              ))}
            </div>
          </div>

          <button
            onClick={submitLeaveRequest}
            disabled={loading}
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-2.5 rounded-xl font-bold text-xs shadow-xs disabled:opacity-50 transition cursor-pointer"
          >
            {loading ? "Submitting..." : "Submit Request"}
          </button>
        </div>
      )}

      {activeTab === "history" && (
        <div className="space-y-3">
          {leaveRecords.map((record) => (
            <div key={record._id} className="border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 rounded-2xl p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="space-y-1">
                <p className="font-bold text-xs text-slate-900 dark:text-white">{record.leaveType} ({record.dates.length} days)</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">{record.startDate} to {record.endDate}</p>
                <p className="text-xs italic text-slate-600 dark:text-slate-300 font-medium">"{record.reason}"</p>
                <div className="flex flex-wrap items-center gap-1.5 pt-1">
                  <span className={`inline-block px-2.5 py-0.5 rounded-lg text-[10px] font-bold ${getStatusColor(record.status)}`}>
                    {record.status.replace(/_/g, ' ').toUpperCase()}
                  </span>
                  {record.status === "pending" && (
                    <span className={`inline-block px-2.5 py-0.5 rounded-lg text-[10px] font-bold ${record.substituteId ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800' : 'bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800'}`}>
                      {record.substituteId ? 'SUBSTITUTION COMPLETED' : 'SUBSTITUTION PENDING'}
                    </span>
                  )}
                </div>
                {record.substituteName && <p className="text-xs mt-1 font-semibold text-slate-700 dark:text-slate-300">Substitute: {record.substituteName}</p>}
                {record.rejectionReason && <p className="text-xs text-rose-600 dark:text-rose-400 mt-1 font-bold">Rejected: {record.rejectionReason}</p>}
              </div>
              <div className="flex flex-row sm:flex-col gap-2 w-full sm:w-auto">
                {record.status === "pending" && !record.substituteId && (
                  <button
                    onClick={() => {
                      const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                      const firstDate = new Date(record.dates[0]);
                      const dayName = daysOfWeek[firstDate.getDay()];

                      sessionStorage.setItem("initialTimetableDay", dayName);
                      sessionStorage.setItem("initialTimetableDate", record.dates[0]);
                      sessionStorage.setItem("substitutingForLeaveId", record._id);
                      sessionStorage.setItem("substitutingForFacultyId", router.state.loginid);

                      setSelectedMenu("MyFacultyTimeTable");
                    }}
                    className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs rounded-xl font-bold transition shadow-xs cursor-pointer"
                  >
                    Assign Substitute
                  </button>
                )}
                {(record.status === "pending" || record.status === "approved_by_hod") && (
                  <button onClick={() => { setLeaveToCancel(record._id); setShowConfirmModal(true); }} className="px-3.5 py-1.5 bg-rose-600 hover:bg-rose-500 text-white text-xs rounded-xl font-bold transition shadow-xs cursor-pointer">
                    Cancel
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Substitute Modal */}
      {showSubstituteModal && (
        <div className="fixed inset-0 bg-slate-950/75 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl w-full max-w-md shadow-xl space-y-4">
            <h3 className="text-base font-bold text-slate-900 dark:text-white">Assign Substitute Faculty</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">Your leave is approved. Please assign a substitute to confirm.</p>
            <select
              value={selectedSubstitute}
              onChange={(e) => setSelectedSubstitute(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
            >
              <option value="">-- Select Substitute --</option>
              {faculties.map(f => <option key={f.employeeId} value={f.employeeId}>{f.firstName} {f.lastName} ({f.department})</option>)}
            </select>
            <div className="flex justify-end space-x-2">
              <button onClick={() => setShowSubstituteModal(false)} className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition cursor-pointer">Cancel</button>
              <button onClick={handleAssignSubstitute} disabled={loading} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold transition shadow-xs cursor-pointer">
                {loading ? "Assigning..." : "Confirm Leave"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-slate-950/75 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl w-full max-w-sm shadow-xl space-y-4">
            <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">Are you sure you want to cancel this request?</p>
            <div className="flex justify-end space-x-2">
              <button onClick={() => setShowConfirmModal(false)} className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition cursor-pointer">No</button>
              <button onClick={async () => {
                const token = localStorage.getItem("token");
                await axios.put(`${baseApiURL()}/faculty/leave/cancel/${leaveToCancel}`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success("Cancelled");
                setShowConfirmModal(false);
                fetchLeaveHistory();
              }} className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-bold transition shadow-xs cursor-pointer">Yes, Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacultyLeaveManagement;